import { FC, useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap-v5'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { showErrorAlert } from '../../../helpers/alert'
import { handleApiError, parseApiErrors } from '../../../utils/handleApiError'
import ImageField from './fields/ImageField'
import { KTSVG } from '../../../../_metronic/helpers'
import { SelectExperienceField } from './fields/SelectExperienceField'
import { createTeam, getDetailTeam, updateTeam } from '../../../services/teamAdminService'
import PhoneInput from 'react-phone-input-2'

type Props = {
    show: boolean;
    onClose: () => void;
    onSubmit?: () => void;
    onLoad?: (isloaded:boolean) => void;
    userSelectedId: number;
    teamId:number;
}

export const MemberModal: FC<Props> = ({ show, onClose, onLoad, onSubmit, userSelectedId,teamId }) => {
    let assigmentsValues: { id: number; checked: boolean }[] = [
        { id: 0, checked: false }, // admin all permissions
        { id: 3, checked: false }, // profile management - 3
        { id: 4, checked: false }, // experience management - 4
        { id: 5, checked: false }, // calendar management - 5
        { id: 6, checked: false }, // mastermind circle management - 6
    ];

    const [initialValues, setInitialValues] = useState({
        user_id: 0,
        email: "",
        first_name: "",
        last_name: "",
        phone_number: "",
        profile_picture: null,
        experience_ids: [],
        assignment: assigmentsValues,
    })

    const emptyValues = {
        user_id: 0,
        email: "",
        first_name: "",
        last_name: "",
        phone_number: "",
        profile_picture: null,
        experience_ids: [],
        assignment: assigmentsValues,
    };

    const getTeam = async () => {
        onLoad(true);
        if (show && userSelectedId > 0) {
            const team_ = await getDetailTeam(teamId,userSelectedId);
            if(team_?.id == 0){
                onClose();
                return
            }
            const userRoleIds = team_?.userRoles?.map(role => role.id) ?? [];
            const isAdminAll = userRoleIds.length === assigmentsValues.length - 1;
            const roles = assigmentsValues.map(p => {
                if (isAdminAll) {
                    return {...p,checked: p.id === 0};
                }
                return {...p,checked: userRoleIds.includes(p.id)};
            });
            console.log({...team_,assignment: roles})
            await setInitialValues({
                user_id: team_?.id ?? 0,
                email: team_?.email ?? "",
                first_name: team_?.firstName ?? "",
                last_name: team_?.lastName ?? "",
                phone_number: team_?.phoneNumber ?? "",
                profile_picture: team_?.profilePicture ?? null,
                experience_ids: team_?.experiencesIds ?? [],
                assignment: roles,
            });
            onLoad(false);
        }
    }

    useEffect(()=>{
        if (userSelectedId === 0) {
            setInitialValues(emptyValues);
        }
        if(userSelectedId > 0){
            getTeam();
        }
    },[userSelectedId])

    const validationSchema = Yup.object({
        user_id: Yup.number(),
        email: Yup.string().email('Wrong email format').min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Email is required'),
        first_name: Yup.string().required("Name is required").max(100, "Max 100 characters"),
        last_name: Yup.string().required("Last name is required").max(30, "Max 30 characters"),
        phone_number: Yup.string().matches(/^[0-9()+\-\s]{7,15}$/,'Invalid phone number').required('Phone is required'),
        profile_picture: Yup.mixed()
            .required("Profile image is required")
            .test("fileOrUrl", "Only images or valid URLs allowed", (value) => {
                if (!value) return false;

                // Caso 1: File
                if (value instanceof File) {
                    return ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(value.type);
                }

                // Caso 2: URL string
                if (typeof value === "string") {
                    try {
                        const url = new URL(value);
                        return true; // si parsea bien, es URL válida
                    } catch {
                        return false;
                    }
                }

                return false;
            })
            .test("fileSize", "Max size is 5MB", (value) => {
                if (value instanceof File) {
                    return value.size <= 5 * 1024 * 1024;
                }
                return true; // si es URL, no aplica límite de tamaño
            }),
        organization: Yup.string(),
        relation: Yup.string(),
        experience_ids: Yup.array().of(Yup.number()),
        assignment: Yup.array().of(
                Yup.object({
                    id: Yup.number().required('Id is required'),
                    checked: Yup.boolean(),
                }),
            ).test(
                'at-least-one-checked',
                'At least one assignment must be selected',
                (arr) => Array.isArray(arr) && arr.some((x) => x?.checked)
            ),
    });

    const handleSubmit = async (values) => {
        try {
            onLoad(true);
            let msg = '';
            let cloneObj = JSON.parse(JSON.stringify({...values}));
            const formData = new FormData();
            formData.append('user_id', values.user_id);
            formData.append('email', values.email);
            formData.append('first_name', values.first_name);
            formData.append('last_name', values.last_name);
            formData.append('phone_number', values.phone_number);
            formData.append('team_id', teamId);
            /* roles */
            delete cloneObj.assignment;
            let roles = values.assignment.filter((a:any) => a.id === 0);
            if(roles.length > 0 && roles[0].checked){
                [3,4,5,6].map(id => {
                    formData.append('roles_ids', id);
                });
            }else{
                values.assignment.filter((a:any) => a.checked).map(id => {
                    formData.append('roles_ids', id);
                });
            }
            /* profile picture */
            if(values.profile_picture instanceof File){
                formData.append('profile_picture', values.profile_picture);
            }
            formData.append('organization', values.organization ?? '');
            formData.append('relation', values.relation ?? '');
            if(values.experience_ids.length > 1){
                let ids = (values.experience_ids ?? []).map((item)=>{
                    formData.append('experience_ids', item);
                });
            }else{
                let ids = (values.experience_ids ?? []).map((item)=>{
                    formData.append('experience_ids[]', item);
                });
            }
            if(values?.user_id == 0){
                delete cloneObj.profile_picture; // photo se maneja aparte en create
                let isCreated = await createTeam(formData);
                msg = isCreated.ok || isCreated.email ? 'The team member has been created successfully.' : 'Unable to create team member.';
            }else{
                let isUpdate = await updateTeam(teamId,values.user_id,formData);
                msg = isUpdate.id ? 'The team member has been updated successfully.' : 'Unable to update team member.';
            }
            onLoad(false);
            onSubmit();
            showErrorAlert({ title: '', message: msg ?? 'The experience has been declined successfully.' });
        } catch (error) {
            onLoad(false);
            handleApiError(error, {
                onServerError: ()=>showErrorAlert({ title: 'Error', message: 'Error declining the experience. Please try again later.' }),
            });
        }
    }

    const handleAssignmentToggle = (index: number,assignments: Assignment[],setFieldValue: (field: string, value: any) => void) => {
        const isAdmin = index === 0
        const nextChecked = !assignments[index].checked

        if (isAdmin) {
            // Si selecciona Administrator → desmarca todos y deja solo admin
            assignments.forEach((_, i) => {
            setFieldValue(`assignment[${i}].checked`, i === 0 ? nextChecked : false)
            })
        } else {
            // Si selecciona un permiso individual → desmarca admin
            setFieldValue(`assignment[0].checked`, false)
            setFieldValue(`assignment[${index}].checked`, nextChecked)
        }
    }

    return (
        <Modal show={show} onHide={onClose} centered size="lg" backdrop="static" dialogClassName="team-admin-add-member-modal">
            <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {(formik) => {
                    const onCancelHandle = () => {
                        setInitialValues(emptyValues);
                        formik.resetForm({ values: emptyValues });
                        onClose();
                    }

                    const handleExperienciesChange = (value:string) => {
                        formik.setFieldValue("experience_ids", value);
                        formik.validateForm();
                    };

                    return (<Form>
                        <div className="team-admin-add-member">
                            {/* HEADER */}
                            <div className="admin-experience-details-header">
                                <h2 className="nb-invite-modal__title">
                                    {formik.values.user_id == 0 ? 'Add New' : 'Edit'} Team
                                </h2>
                                <div className="rm-style-3 cursor-pointer" onClick={onClose}>
                                    <KTSVG path="/media/svg/nobilis/closeX.svg" className="rm-style-4" />
                                </div>
                                {/* <div className="admin-experience-details-close">
                                </div> */}
                            </div>
                            {/* CONTENT */}
                            <div className="team-admin-add-member__card">
                                <div className="team-admin-add-member__section">
                                    <div className="team-admin-add-member__section-title">
                                        Information
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Name</label>
                                        <div className="team-admin-add-member__input">
                                            <input name="first_name" value={formik.values.first_name} onChange={formik.handleChange} className="tap-add-experience-2-input" maxLength={100} />
                                        </div>
                                        <ErrorMessage name="first_name" component="div" className="tap-add-experience-text text-danger" />
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Surname</label>
                                        <div className="team-admin-add-member__input">
                                            <input name="last_name" value={formik.values.last_name} onChange={formik.handleChange} className="tap-add-experience-2-input" maxLength={100} />
                                        </div>
                                        <ErrorMessage name="last_name" component="div" className="tap-add-experience-text text-danger" />
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Email</label>
                                        <div className="team-admin-add-member__input">
                                            <input name="email" value={formik.values.email} onChange={formik.handleChange} className="tap-add-experience-2-input" maxLength={100} />
                                        </div>
                                        <ErrorMessage name="email" component="div" className="tap-add-experience-text text-danger" />
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Phone number</label>
                                        <div className="tap-add-experience-2-input-line">
                                            <Field name='phone_number'>
                                                {({ field, form }: any) => (
                                                    <PhoneInput
                                                    country='us'
                                                    placeholder='+1'
                                                    containerClass='profile-input'
                                                    value={field.value || ''}
                                                    onChange={(val: string) => form.setFieldValue(field.name, val)}
                                                    onBlur={() => form.setFieldTouched(field.name, true)}
                                                    />
                                                )}
                                            </Field>
                                        </div>
                                        <ErrorMessage name="phone_number" component="div" className="tap-add-experience-text text-danger" />
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Photo</label>
                                        <ImageField />
                                        <ErrorMessage name="profile_picture" component="div" className="tap-add-experience-text text-danger" />
                                    </div>
                                </div>

                                {/* ASSIGN EXPERIENCE */}
                                <div className="team-admin-add-member__field">
                                    <label>Assign experience</label>
                                    <SelectExperienceField values={formik.values.experience_ids} onChange={handleExperienciesChange} name='experience_ids'/>
                                    <ErrorMessage name="experience_ids" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                            </div>

                            {/* Assignment */}
                            <div className="team-assignment">
                                <div className="team-assignment-label">Assignment</div>
                                <div className="team-assignment-hint">Select all that apply</div>
                                <div className="team-assignment-box">
                                    <div className='pf-checkbox'>
                                        {/* <Field type='checkbox' name={`assignment[0].checked`} className='pf-check' id="assignment-0" /> */}
                                        <input type="checkbox" id="assignment-0" className="pf-check" checked={formik.values.assignment[0].checked} 
                                            onChange={() => handleAssignmentToggle(0,formik.values.assignment,formik.setFieldValue)}
                                        />
                                        <label htmlFor="assignment-0" className="pf-checkbox-content">
                                            <div className="assignment-title">Administrator</div>
                                            <div className="assignment-description">
                                                <ul>
                                                    {/* <li>Team Members Assignation</li> */}
                                                    <li>Profile Management</li>
                                                    <li>Experience Management</li>
                                                    <li>Calendar Management</li>
                                                    <li>Mastermind Circle Management</li>
                                                </ul>
                                            </div>
                                        </label>
                                    </div>
                                    <div className='pf-checkbox'>
                                        {/* <Field type='checkbox' name={`assignment[1].checked`} className='pf-check' id="assignment-1" /> */}
                                        <input type="checkbox" id="assignment-1" className="pf-check" checked={formik.values.assignment[1].checked}
                                            onChange={() => handleAssignmentToggle(1,formik.values.assignment,formik.setFieldValue)}
                                        />
                                        <label htmlFor="assignment-1" className="pf-checkbox-content">
                                            <div className="assignment-title">Profile Management</div>
                                            <div className="assignment-description"></div>
                                        </label>
                                    </div>
                                    <div className='pf-checkbox'>
                                        {/* <Field type='checkbox' name={`assignment[2].checked`} className='pf-check' id="assignment-2" /> */}
                                        <input type="checkbox" id="assignment-2" className="pf-check" checked={formik.values.assignment[2].checked}
                                            onChange={() => handleAssignmentToggle(2,formik.values.assignment,formik.setFieldValue)}
                                        />
                                        <label htmlFor="assignment-2" className="pf-checkbox-content">
                                            <div className="assignment-title">Experience Management</div>
                                            <div className="assignment-description"></div>
                                        </label>
                                    </div>
                                    <div className='pf-checkbox'>
                                        {/* <Field type='checkbox' name={`assignment[3].checked`} className='pf-check' id="assignment-3" /> */}
                                        <input type="checkbox" id="assignment-3" className="pf-check" checked={formik.values.assignment[3].checked}
                                            onChange={() => handleAssignmentToggle(3,formik.values.assignment,formik.setFieldValue)}
                                        />
                                        <label htmlFor="assignment-3" className="pf-checkbox-content">
                                            <div className="assignment-title">Calendar Management</div>
                                            <div className="assignment-description"></div>
                                        </label>
                                    </div>
                                    <div className='pf-checkbox'>
                                        {/* <Field type='checkbox' name={`assignment[4].checked`} className='pf-check' id="assignment-4" /> */}
                                        <input type="checkbox" id="assignment-4" className="pf-check" checked={formik.values.assignment[4].checked}
                                            onChange={() => handleAssignmentToggle(4,formik.values.assignment,formik.setFieldValue)}
                                        />
                                        <label htmlFor="assignment-4" className="pf-checkbox-content">
                                            <div className="assignment-title">Mastermind Circle Management</div>
                                            <div className="assignment-description"></div>
                                        </label>
                                    </div>
                                    <ErrorMessage name="assignment">
                                        {(msg) => (typeof msg === 'string' ? <div className="tap-add-experience-text text-danger">{msg}</div> : null)}
                                    </ErrorMessage>
                                </div>
                            </div>

                            {/* FOOTER */}
                            <div className="team-admin-add-member__footer">
                                <button type='button' className="decline-experience__btn decline-experience__btn--secondary" onClick={onCancelHandle}>
                                    Cancel
                                </button>

                                <button type='submit' className="decline-experience__btn decline-experience__btn--primary">
                                    {formik.values.user_id == 0 ? 'Add' : 'Edit'} Member
                                    &nbsp;<KTSVG path='/media/svg/nobilis/vector02.svg' />
                                </button>
                            </div>
                        </div>
                    </Form>);
                }}
            </Formik>
        </Modal>
    )
}
